/* eslint-disable no-console */
import { debounce } from '@/lib/utils';
import { ON_CHANGE_DEBOUNCE_TIMER } from './constants';
import { subscribe, publish } from './pubsub';
import { PUB_SUB_EVENTS } from './constants';
import CartPerformance from './cart-performance';
import fetchConfig from './fetch-config';
import { trapFocus } from './trap-focus';
import CartRemoveButton from './cart-remove-button';

interface CartState {
  item_count: number;
  errors?: string;
  sections?: Record<string, string>;
  items?: Array<{ title: string; quantity: number }>;
  [key: string]: unknown;
}

interface CartProductVariant {
  id: number;
  available: boolean;
  inventory_management: string | null;
}

interface CartProductResponse {
  variants: CartProductVariant[];
}

interface CartItemResponse {
  id: number;
  key: string;
  title: string;
  handle: string;
  variant_id: number;
  product_id: number;
  properties: Record<string, string>;
}

export default class CartItems extends HTMLElement {
  lineItemStatusElement: HTMLElement | null = null;

  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    const quantityInputs = this.querySelectorAll('[id^="Cart-quantity-"]');
    quantityInputs.forEach((input) => {
      input.addEventListener('change', debouncedOnChange.bind(this));
    });
  }

  cartUpdateUnsubscriber: (() => void) | undefined = undefined;

  connectedCallback() {
    this.checkUnavailableItems();

    const checkoutButton = document.getElementById('CartDrawer-Checkout');

    checkoutButton?.addEventListener('click', async (event) => {
      event.preventDefault();

      await this.removeUnavailableItemsBeforeCheckout();

      await this.onCartUpdate();

      const form = checkoutButton.closest('form') as HTMLFormElement;

      if (form) {
        form.submit();
      } else {
        window.location.href = '/checkout';
      }
    });

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  private async removeUnavailableItemsBeforeCheckout() {
    const response = await fetch('/cart.js');
    const cart: { items: CartItemResponse[] } = await response.json();

    const updates: Record<string, number> = {};

    await Promise.all(
      cart.items.map(async (item) => {
        const productResponse = await fetch(`/products/${item.handle}.js`);
        const product: CartProductResponse = await productResponse.json();

        const variant = product.variants.find((variantItem) => variantItem.id === item.variant_id);

        if (!variant || variant.available === false) {
          updates[item.key] = 0;

          cart.items.forEach((cartItem) => {
            if (cartItem.properties?.['_Product ID'] === `${item.product_id}-${item.variant_id}`) {
              updates[cartItem.key] = 0;
            }
          });
        }
      }),
    );

    if (Object.keys(updates).length === 0) {
      return;
    }

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    await this.onCartUpdate();
  }
  private checkUnavailableItems() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart: { items: CartItemResponse[] }) => {
        cart.items.forEach((item) => {
          fetch(`/products/${item.handle}.js`)
            .then((response) => response.json())
            .then((product: CartProductResponse) => {
              const variant = product.variants.find((variantItem) => variantItem.id === item.variant_id);

              if (!variant || variant.available) {
                return;
              }

              this.showUnavailableMessage(item.variant_id);
            });
        });
      });
  }

  private showUnavailableMessage(variantId: number) {
    const article = document.querySelector<HTMLElement>(`article[data-variant-id="${variantId}"]`);

    if (!article) return;

    const wrapper = article.querySelector<HTMLElement>('[data-unavailable-wrapper]');
    const message = article.querySelector<HTMLElement>('[data-unavailable-message]');

    if (!wrapper || !message) return;

    wrapper.style.height = '0';
    message.textContent = '';

    const locale = window.location.pathname.split('/')[1]?.split('-')[0] || 'fi';

    const messages: Record<string, string> = {
      fi: 'Pahoittelut, tämä tuote ei ole enää saatavilla.',
      sv: 'Vi beklagar, den här produkten är inte längre tillgänglig.',
      de: 'Leider ist dieses Produkt nicht mehr verfügbar.',
    };

    message.textContent = messages[locale] || messages.fi;
    wrapper.style.height = '32px';
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id: string) {
    if (!id) return;

    const inputs = this.querySelectorAll(`#Cart-quantity-${id}`);

    inputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        const value = input.getAttribute('value');
        if (value !== null) {
          input.value = value;
        }
      }
    });

    this.isEnterPressed = false;
  }

  setValidity(event: Event, index: string, message: string) {
    if (!event.target || !(event.target instanceof HTMLInputElement)) return;

    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event: Event) {
    if (!event.target || !(event.target instanceof HTMLInputElement)) return;

    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    if (!index) return;

    // If a parent item's quantity is reduced, clamp any linked upsell items (e.g. pre-installed SSD)
    // so they can't exceed the number of parent products in cart.
    const isUpsellItem = !!event.target.closest('[data-warranty-item="true"]');
    const parentCartItem = event.target.closest<HTMLElement>('article[id^="CartItem-"]');
    const parentItemKey = parentCartItem?.dataset?.itemKey;

    let message = '';

    const min = parseInt(event.target.dataset.min || '0');
    const max = parseInt(event.target.max || '999');
    const step = parseInt(event.target.step || '1');

    if (inputValue < min) {
      if (window.quickOrderListStrings?.min_error) {
        message = window.quickOrderListStrings.min_error.replace('[min]', min.toString());
      }
    } else if (inputValue > max) {
      if (window.quickOrderListStrings?.max_error) {
        message = window.quickOrderListStrings.max_error.replace('[max]', max.toString());
      }
    } else if (inputValue % step !== 0) {
      if (window.quickOrderListStrings?.step_error) {
        message = window.quickOrderListStrings.step_error.replace('[step]', step.toString());
      }
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();

      const activeElement = document.activeElement;
      const name = activeElement?.getAttribute('name') || '';
      const variantId = event.target.dataset.quantityVariantId || '';

      // If the changed input is a parent item in the cart drawer, update the parent + all linked upsells
      // in a single /cart/update request (prevents missing one due to drawer re-render during iteration).
      if (!isUpsellItem && parentItemKey) {
        const updates: Record<string, number> = { [parentItemKey]: inputValue };

        const linkedUpsellItems = document.querySelectorAll<HTMLElement>(
          `[data-warranty-item="true"][data-linked-item-key="${CSS.escape(parentItemKey)}"]`,
        );

        linkedUpsellItems.forEach((el) => {
          const upsellKey = el.dataset.itemKey;
          if (!upsellKey) return;

          const linkedQtyInput = el.querySelector<HTMLInputElement>('input[id^="Cart-quantity-"][data-index]');
          const linkedCurrentQty = parseInt(linkedQtyInput?.value || '0');

          // Clamp down if the linked upsell is above the new parent quantity.
          if (linkedCurrentQty > inputValue) {
            updates[upsellKey] = inputValue;
          }
        });

        const body = JSON.stringify({
          updates,
          sections: this.getSectionsToRender()
            .map((section) => section.section)
            .filter(Boolean),
          sections_url: window.location.pathname,
        });

        const url = window.routes?.cart_update_url;
        if (!url) {
          console.warn('Cart update URL not available');
          return;
        }

        this.enableLoading(index);
        this.fetchAndHandleCartUpdate(url, body, index, 'change', event, variantId, name);
        return;
      }

      this.updateQuantity(index, inputValue, event, undefined, name, variantId);
    }
  }

  onChange(event: Event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      if (!window.routes?.cart_url) return;

      return fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          if (!responseText) return;

          try {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const selectors = ['cart-drawer-items', '.cart-drawer__footer'];

            for (const selector of selectors) {
              const targetElement = document.querySelector(selector);
              const sourceElement = html.querySelector(selector);
              if (targetElement && sourceElement) {
                targetElement.replaceWith(sourceElement);
              }
            }
          } catch (error) {
            console.warn('Error parsing cart drawer HTML:', error);
          }
        })
        .catch((e) => {
          console.error('Error updating cart drawer:', e);
        });
    } else {
      if (!window.routes?.cart_url) return;

      return fetch(`${window.routes.cart_url}?section_id=custom-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          if (!responseText) return;

          try {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('cart-items');
            if (sourceQty) {
              this.innerHTML = sourceQty.innerHTML;
            }
          } catch (error) {
            console.warn('Error parsing cart items HTML:', error);
          }
        })
        .catch((e) => {
          console.error('Error updating cart items:', e);
        });
    }
  }

  getSectionsToRender() {
    const mainCartFooter = document.getElementById('main-cart-footer');
    const footerSectionId = mainCartFooter?.dataset?.id || '';

    return [
      {
        id: 'custom-main-cart-items',
        section: 'custom-main-cart-items',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: footerSectionId,
        selector: '.js-contents',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart__inner',
      },
    ];
  }

  updateQuantity(line: string, quantity: number, event: Event, lineItemKey: string, name: string, variantId: string) {
    if (!line || !event) return;

    this.enableLoading(line);

    const body = JSON.stringify({
      ...(lineItemKey ? { id: lineItemKey } : { line: line }),
      quantity,
      sections: this.getSectionsToRender()
        .map((section) => section.section)
        .filter(Boolean),
      sections_url: window.location.pathname,
    });

    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const url = window.routes?.cart_change_url;

    if (!url) {
      console.warn('Cart change URL not available');
      this.disableLoading(line);
      return;
    }

    this.fetchAndHandleCartUpdate(url, body, line, eventTarget, event, variantId, name);
  }

  removeWarrantyAndItemQuantity(
    line: string,
    itemLineItemKey: string,
    warrantyLineItemKeys: string[],
    event: Event,
    variantId: string,
  ) {
    const updates: { [key: string]: number } = {};

    if (itemLineItemKey) {
      updates[itemLineItemKey] = 0;
    }

    warrantyLineItemKeys.forEach((key) => {
      updates[key] = 0;
    });

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender()
        .map((section) => section.section)
        .filter(Boolean),
      sections_url: window.location.pathname,
    });

    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const url = window.routes?.cart_update_url;

    if (!url) {
      console.warn('Cart update URL not available');
      this.disableLoading(line);
      return;
    }

    this.fetchAndHandleCartUpdate(url, body, line, eventTarget, event, variantId, '');
  }

  private fetchAndHandleCartUpdate(
    url: string,
    body: string,
    line: string,
    eventTarget: string,
    event: Event,
    variantId: string,
    name: string,
  ) {
    fetch(url, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => this.handleCartResponse(state, line, eventTarget, event, variantId, name))
      .catch((error) => this.handleCartError(error))
      .finally(() => this.disableLoading(line));
  }

  private handleCartResponse(
    state: string,
    line: string,
    eventTarget: string,
    event: Event,
    variantId: string,
    name: string,
  ) {
    if (!state) return;

    let parsedState;
    try {
      parsedState = JSON.parse(state);
    } catch (error) {
      console.warn('Error parsing cart state:', error);
      this.disableLoading(line);
      return;
    }

    CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
      this.updateCartSections(parsedState, line);
      this.handleFocusManagement(parsedState, line, name);
    });

    CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'cart-items',
      cartData: parsedState,
      variantId: variantId,
    });
  }

  private updateCartSections(parsedState: CartState, line: string) {
    const quantityElements = document.querySelectorAll(`#Cart-quantity-${line}`);

    if (parsedState.errors) {
      this.resetQuantityInputs(quantityElements);
      this.updateLiveRegions(line, parsedState.errors);
      return;
    }

    this.updateEmptyState(parsedState);
    this.updateSectionContents(parsedState);
    this.updateQuantityMessages(parsedState, line, quantityElements);
  }

  private resetQuantityInputs(quantityElements: NodeListOf<Element>) {
    quantityElements.forEach((quantityElement) => {
      if (quantityElement instanceof HTMLInputElement) {
        const value = quantityElement.getAttribute('value');
        if (value !== null) {
          quantityElement.value = value;
        }
      }
    });
  }

  private updateEmptyState(parsedState: CartState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
  }

  private updateSectionContents(parsedState: CartState) {
    this.getSectionsToRender().forEach((section) => {
      if (!section.section) return;

      let elementToReplace;
      const sectionElement = document.getElementById(section.id);

      if (sectionElement?.querySelector(section.selector)) {
        elementToReplace = sectionElement.querySelector(section.selector);
      } else {
        elementToReplace = sectionElement;
      }

      if (elementToReplace && parsedState.sections?.[section.section]) {
        const sectionHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        if (sectionHTML) {
          elementToReplace.innerHTML = sectionHTML;
        }
      }
    });
  }

  private updateQuantityMessages(parsedState: CartState, line: string, quantityElements: NodeListOf<Element>) {
    const items = document.querySelectorAll('[id*="-cart-item"]');
    const updateItem = parsedState.items?.[parseInt(line) - 1];
    const updatedValue = updateItem?.quantity;
    let message = '';

    quantityElements.forEach((quantityElement) => {
      if (
        quantityElement instanceof HTMLInputElement &&
        items.length === parsedState.items?.length &&
        updatedValue !== parseInt(quantityElement.value)
      ) {
        if (typeof updatedValue === 'undefined') {
          if (window.cartStrings?.error) {
            message = window.cartStrings.error;
          }
        } else {
          if (window.cartStrings?.quantityError) {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue.toString());
          }
        }
      }

      if (updateItem) {
        this.announceItemCountChange(updateItem.title, updateItem.quantity);
      }

      this.updateLiveRegions(line, message);
    });
  }

  private handleFocusManagement(parsedState: CartState, line: string, name: string) {
    const lineItems = document.querySelectorAll(`#CartItem-${line}`);
    const cartDrawerWrapper = document.querySelector('cart-drawer');

    lineItems.forEach((lineItem) => {
      if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
        const nameElement = lineItem.querySelector(`[name="${name}"]`) as HTMLElement;
        if (cartDrawerWrapper && nameElement) {
          trapFocus(cartDrawerWrapper, nameElement);
        } else if (nameElement) {
          nameElement.focus();
        }
      } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
        const emptyCart = cartDrawerWrapper.querySelector('.cart__inner-empty');
        const emptyCartLink = cartDrawerWrapper.querySelector('a');
        if (emptyCart && emptyCartLink) {
          trapFocus(emptyCart, emptyCartLink);
        }
      } else if (document.querySelector('[id*="-cart-item"]') && cartDrawerWrapper) {
        const cartItemName = document.querySelector('.cart-item__name') as HTMLElement;
        if (cartItemName) {
          trapFocus(cartDrawerWrapper, cartItemName);
        }
      }
    });
  }

  private handleCartError(error: unknown) {
    console.log('Error updating quantity:', error);
    this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors && window.cartStrings?.error) {
      errors.textContent = window.cartStrings.error;
    }
  }

  announceItemCountChange(product: string, quantity: number) {
    const announcer = document.querySelector('#CartDrawer-LiveLineStatusChange');

    if (!announcer) return;

    const message = window.cartStrings.updated_line_quantity
      .replace('[product]', product)
      .replace('[quantity]', quantity.toString());

    announcer.innerHTML = message;
  }

  updateLiveRegions(line: string, message: string) {
    if (!line || !this.lineItemStatusElement) return;

    const lineItemErrors = document.querySelectorAll(`#CartItem-LineItemError-${line}`);

    lineItemErrors.forEach((lineItemError) => {
      const errorText = lineItemError?.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.textContent = message;
      }
    });

    this.lineItemStatusElement.setAttribute('aria-hidden', 'true');

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');

    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  getSectionInnerHTML(html: string, selector: string): string | null {
    if (!html || !selector) return null;

    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const element = parsed.querySelector(selector);
      return element?.innerHTML || null;
    } catch (error) {
      console.warn('Error parsing section HTML:', error);
      return null;
    }
  }

  enableLoading(line: string) {
    if (!line) return;

    const mainCartItems =
      document.getElementById('custom-main-cart-items') || document.getElementById('CartDrawer-CartItems');

    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);

    [...cartItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', 'false');
    }
  }

  disableLoading(line: string) {
    if (!line) return;

    const mainCartItems =
      document.getElementById('custom-main-cart-items') || document.getElementById('CartDrawer-CartItems');

    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}
